/** venture/report.ts — 조합 결성등록 후 VICS 정기(반기)·결산 보고 일정과 상태 (순수 함수, 2026-09-24 지도사 확정)
 *  정기보고: 상반기분 7/1~7/31 · 하반기분 익년 1/1~1/31 — VICS 로그인 후 [1329] 검증·확정/전송 (변동 없으면 버튼만)
 *  결산보고: 익년 1/1~3/31 [1340] — 결산서+회계법인 감사의견서, 또는 면제 시 자금운용현황(별지 제9호)
 *  감사의견서 면제: ① 사업연도 투자실적 변동 없음 ② 결성 후 3년이 지나지 않은 조합으로 출자금 총액 2억원 이하
 *  결성등록일은 8단계 s8.txt._regdate(등록원부 등록일자) · 저장소 s15: chk.half_<y>H1 / half_<y>H2 / fin_<y>(완료) · chk.nochange_<y> · up.fin_<y>_audit · up.fs_<y>_pdf/docx */
import { getTxt, getChk } from './stepcommon';
import { won } from './coterms';
import type { Project } from './types';

export type RepKind = 'half' | 'annual';
export type RepStat = 'upcoming' | 'due' | 'late' | 'done';
export interface RepPeriod { key: string; kind: RepKind; year: number; half: 0 | 1 | 2; label: string; covers: string; from: string; to: string; stat: RepStat; }
export interface RepAlert { stat: 'due' | 'late'; list: RepPeriod[]; label: string; }

const pad = (n: number): string => String(n).padStart(2, '0');
export const ymd = (d: Date): string => d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate());
export const todayYmd = (): string => ymd(new Date());
export const AUDIT_CAP = 200000000;   // 출자금 총액 2억원

/** 결성등록 완료 = 7단계(조합결성 등록) 완료 → 8단계 이상 */
export const isRegistered = (p: Project): boolean => !!p.done || p.step >= 8;

/** 'YYYY.M.D' · 'YYYY-MM-DD' 등 → 'YYYY-MM-DD' (문자열 비교용) · 못 읽으면 '' */
export function isoDate(s: string): string {
  const m = (s || '').match(/(\d{4})\D+(\d{1,2})\D+(\d{1,2})/) || (s || '').match(/^(\d{4})(\d{2})(\d{2})$/);
  return m ? m[1] + '-' + pad(parseInt(m[2], 10)) + '-' + pad(parseInt(m[3], 10)) : '';
}

/** 결성등록일 — 8단계 조합 등록정보의 등록일자(등록원부) → (구) 15줄 입력 → 마스터 신청일 → 프로젝트 등록일 순 */
export function regDateOf(p: Project): { date: string; src: 'input' | 'apply' | 'project' | '' } {
  const t = isoDate(getTxt(p, 's8', '_regdate')) || isoDate(getTxt(p, 's15', 'regDate'));
  if (t) return { date: t, src: 'input' };
  const a = p.master ? isoDate(p.master.applyDate || '') : '';
  if (a) return { date: a, src: 'apply' };
  const r = isoDate(p.regDate || '');
  if (r) return { date: r, src: 'project' };
  return { date: '', src: '' };
}

export const repKey = (kind: RepKind, year: number, half: number): string => (kind === 'annual' ? 'fin_' + year : 'half_' + year + 'H' + half);

function statOf(done: boolean, from: string, to: string, today: string): RepStat {
  if (done) return 'done';
  if (today < from) return 'upcoming';
  return today > to ? 'late' : 'due';
}

/** 결성등록일부터 올해까지의 보고 기간 목록 (기간 시작 순) */
export function periods(p: Project, today: string = todayYmd()): RepPeriod[] {
  const r = regDateOf(p).date;
  if (!r) return [];
  const ry = parseInt(r.slice(0, 4), 10); const ty = parseInt(today.slice(0, 4), 10);
  const out: RepPeriod[] = [];
  const mk = (kind: RepKind, year: number, half: 0 | 1 | 2, from: string, to: string, label: string, covers: string) => {
    const key = repKey(kind, year, half);
    out.push({ key, kind, year, half, label, covers, from, to, stat: statOf(getChk(p, 's15', key), from, to, today) });
  };
  for (let y = ry; y <= ty; y++) {
    if (r <= y + '-06-30') mk('half', y, 1, y + '-07-01', y + '-07-31', y + '년 상반기 정기보고', y + '.1.1 ~ 6.30 운영상황');
    if (r <= y + '-12-31') {
      mk('half', y, 2, (y + 1) + '-01-01', (y + 1) + '-01-31', y + '년 하반기 정기보고', y + '.7.1 ~ 12.31 운영상황');
      mk('annual', y, 0, (y + 1) + '-01-01', (y + 1) + '-03-31', y + '년 결산보고', y + ' 사업연도 결산');
    }
  }
  return out.sort((a, b) => (a.from < b.from ? -1 : a.from > b.from ? 1 : a.kind === 'half' ? -1 : 1));
}

const short = (x: RepPeriod): string => (x.kind === 'annual' ? x.year + ' 결산' : x.year + ' ' + (x.half === 1 ? '상' : '하') + '반기');

/** 목록 색칠·배지용 — 지연이 있으면 지연, 아니면 기간 중, 없으면 null */
export function repAlert(p: Project, today: string = todayYmd()): RepAlert | null {
  if (!isRegistered(p)) return null;
  const ps = periods(p, today);
  const late = ps.filter((x) => x.stat === 'late');
  if (late.length) return { stat: 'late', list: late, label: '보고 지연 · ' + late.map(short).join(', ') };
  const due = ps.filter((x) => x.stat === 'due');
  if (due.length) {
    // 정기(1/31)와 결산(3/31)이 같은 1월에 겹치므로 종류마다 제 마감일을 붙인다
    const mmdd = (iso: string): string => iso.slice(5).replace('-', '/');
    return { stat: 'due', list: due, label: '보고 기간 · ' + due.map((x) => (x.kind === 'annual' ? '결산 ~' : '정기 ~') + mmdd(x.to)).join(' · ') };
  }
  return null;
}

/** 다음(가장 가까운) 보고 — 15줄 요약용 */
export function nextPeriod(p: Project, today: string = todayYmd()): RepPeriod | null {
  const ps = periods(p, today).filter((x) => x.stat !== 'done');
  return ps.length ? ps[0] : null;
}

export interface AuditRule { exempt: boolean; noChange: boolean; within3y: boolean; small: boolean; reasons: string[]; }
function addYears(iso: string, n: number): string { return (parseInt(iso.slice(0, 4), 10) + n) + iso.slice(4); }

/** 결산보고 감사의견서 면제 판정 (사업연도 y · 보고 시점 = 익년 3/31 기준) */
export function auditRule(p: Project, y: number): AuditRule {
  const noChange = getChk(p, 's15', 'nochange_' + y);
  const r = regDateOf(p).date;
  const within3y = !!r && ((y + 1) + '-03-31') < addYears(r, 3);
  const total = won(p.total);
  const small = total > 0 && total <= AUDIT_CAP;
  const reasons: string[] = [];
  if (noChange) reasons.push('사업연도 투자실적 변동 없음');
  if (within3y && small) reasons.push('결성 후 3년 이내 · 출자금 총액 2억원 이하');
  return { exempt: noChange || (within3y && small), noChange, within3y, small, reasons };
}

export const REP_STAT_KO: Record<RepStat, string> = { upcoming: '예정', due: '기간 중', late: '지연', done: '완료' };
export const REP_STAT_PILL: Record<RepStat, string> = { upcoming: 'p-gray', due: 'p-amber', late: 'p-red', done: 'p-green' };
